import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ErrorIndicatorProps {
  error?: string;
  fieldId: string;
  children: React.ReactNode;
}

export function ErrorIndicator({ error, fieldId, children }: ErrorIndicatorProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  // Reset tooltip visibility when error changes
  React.useEffect(() => {
    setIsTooltipVisible(false);
  }, [error]);

  const child = React.Children.only(children) as React.ReactElement;
  const enhanced = React.cloneElement(child, {
    className: cn((child.props as { className?: string }).className, error ? 'border-red-500' : ''),
    'aria-invalid': error ? ('true' as const) : undefined,
    'aria-describedby': error ? `${fieldId}-error` : undefined,
  });

  return (
    <div className="relative">
      <div className="relative">
        {enhanced}
        {error && (
          <div
            className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-red-600 z-10 cursor-pointer"
            onMouseEnter={() => setIsTooltipVisible(true)}
            onMouseLeave={() => setIsTooltipVisible(false)}
            aria-label="Show error"
          >
            <AlertCircle className="h-4 w-4" fill="white" />
          </div>
        )}
      </div>
      {error && isTooltipVisible && (
        <div className="absolute right-0 top-full mt-1 z-50 pointer-events-none" aria-live="polite">
          <div className="relative">
            <div className="absolute -top-1 right-3 w-2 h-2 bg-red-600 rotate-45" />
            <div
              id={`${fieldId}-error`}
              className="relative bg-red-600 text-white text-xs font-medium px-2 py-1 rounded-md w-64"
            >
              <span>{error}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ErrorIndicator;
